import { motion } from 'motion/react';
import { glowColorFor } from './RevealBackground';

export interface WinnerBadgeBackdropProps {
  /** Badge name from `mark_as_opened` — rendered as huge outlined
   *  text behind the winner card (e.g. "GRAIL" in gold). */
  badge: string;
  /** Off until the orchestrator says the card image stage is on.
   *  Keeps the GRAIL backdrop + central bloom hidden through the
   *  title beat, then lifts both in step with the card. */
  visible: boolean;
  /** Returning view — paint the bloom + outline text directly in
   *  their visible targets (no bloom spread, no centre-out letter
   *  reveal) with a 0 s transition. If `visible` is false it stays
   *  hidden. Default false. */
  instant?: boolean;
}

/**
 * Giant outline-only badge name in the winner-screen background —
 * Figma 745:72070.
 *
 * Two stacked layers:
 *   • Glow bloom — soft radial gradient from the centre in the
 *     badge's signature colour, screen-blended so it brightens
 *     anything underneath. Scales OUT from a small disc so the
 *     light feels "born" with the card.
 *   • Outline text — huge uppercase Bebas Neue letters, no fill,
 *     stroke in the same signature colour. A radial mask fades
 *     the letters out toward the viewport edges so they integrate
 *     with the dark ambient and don't slam the corners.
 *
 * Both layers are gated by `visible`: hidden through the title beat
 * and animated in together with the card image.
 *
 * Layered behind all WinnerStep content via `absolute inset-0` +
 * `pointer-events-none` so clicks pass through to the card and
 * Buyback CTA underneath.
 */
export function WinnerBadgeBackdrop({
  badge,
  visible,
  instant = false,
}: WinnerBadgeBackdropProps) {
  const color = glowColorFor(badge);

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {/* Glow bloom — a single radial gradient using the badge
          colour. `mixBlendMode: 'screen'` brightens (rather than
          tints) anything under it, so it reads as light rather
          than a coloured overlay. `scale` from 0.55 → 1 makes the
          aura look like it's spreading from the centre, not just
          fading in. */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${color}, transparent 60%)`,
          mixBlendMode: 'screen',
          willChange: 'opacity, transform',
        }}
        initial={
          instant
            ? visible
              ? { opacity: 0.32, scale: 1 }
              : { opacity: 0, scale: 0.55 }
            : { opacity: 0, scale: 0.55 }
        }
        animate={
          visible
            ? { opacity: 0.32, scale: 1 }
            : { opacity: 0, scale: 0.55 }
        }
        transition={
          instant ? { duration: 0 } : { duration: 1.4, ease: [0.16, 1, 0.3, 1] }
        }
      />

      {/* Outline text. Lives in its own motion layer so the bloom
          can lead by a beat. Two animations compose:
            • outer div fades opacity 0 → 0.55 (short, mostly to
              soften the entrance);
            • inner span widens `scaleX` from 0 → 1.18 anchored
              at the centre, so the letters "open out" left and
              right from a single vertical sliver — that's the
              "появляется из центра к краям" feel. */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          // Radial mask: full opacity around the centre line, fading
          // to transparent toward the viewport edges. Ellipse so wide
          // tablets/desktops don't show hard cut-offs on the sides.
          maskImage:
            'radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 88%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 88%)',
        }}
        initial={{ opacity: instant ? (visible ? 0.55 : 0) : 0 }}
        animate={{ opacity: visible ? 0.55 : 0 }}
        transition={
          instant
            ? { duration: 0 }
            : {
                duration: 0.55,
                ease: 'easeOut',
                delay: visible ? 0.1 : 0,
              }
        }
      >
        <motion.span
          className="uppercase block"
          style={{
            // Bebas Neue — single-weight (400), condensed, all-caps
            // display face. Classic "sports-card / poster headline"
            // look. Far narrower than Inter Bold, so the badge name
            // reads as DRAMATIC display lettering rather than a
            // generic web headline — much closer to the Figma
            // concept (745:73397) than the spec's Inter Bold call.
            fontFamily:
              '"Bebas Neue", "Reddit Sans Condensed", "Inter", sans-serif',
            // Bebas Neue is shipped only at 400 — its glyphs already
            // read heavy at display sizes; bumping weight is a no-op
            // and would force a fallback render.
            fontWeight: 400,
            // Figma 745:73397 uses 480.31 px at a 1560 px frame —
            // ≈ 31 vw. Adapt to viewport with clamp so it stays
            // readable from mobile up to 4k.
            fontSize: 'clamp(220px, 40vw, 480px)',
            // Looser tracking opens the air between letters — Bebas's
            // tight default kerning packs the word tight, which read
            // too cramped at this display scale.
            letterSpacing: '0.04em',
            // `scaleX` is owned by motion — the final stretched-1.18
            // form is its animation target, see `animate`.
            // `transform-origin: center` keeps the widen anchored to
            // the centre line of the headline.
            transformOrigin: 'center',
            // Outline-only: transparent fill + colour stroke gives
            // the "ghost letters" look. Bold-weight strokes already
            // read dense, so a 2 px stroke stays elegant rather than
            // marker-heavy.
            color: 'transparent',
            WebkitTextStroke: `2px ${color}`,
            whiteSpace: 'nowrap',
            lineHeight: 1,
            willChange: 'transform',
          }}
          initial={{ scaleX: instant ? (visible ? 1.18 : 0) : 0 }}
          animate={{ scaleX: visible ? 1.18 : 0 }}
          transition={
            instant ? { duration: 0 } : { duration: 1.0, ease: [0.16, 1, 0.3, 1] }
          }
        >
          {badge}
        </motion.span>
      </motion.div>
    </div>
  );
}
